import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { T } from '@/hooks/useAutoTranslate';

interface FAQItem {
  id: number;
  question_zh: string;
  question_en: string;
  question_ru: string;
  answer_zh: string;
  answer_en: string;
  answer_ru: string;
  category: string;
}

const categories = ['全部', '流程', '医疗', '法律', '费用'];

const LANG_MAP: Record<string, 'zh' | 'en' | 'ru'> = {
  zh: 'zh',
  en: 'en',
  ru: 'ru',
};

export default function FAQSection() {
  const { t, i18n } = useTranslation();
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [activeCat, setActiveCat] = useState('全部');
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lang: 'zh' | 'en' | 'ru' = LANG_MAP[i18n.language] || 'zh';

  const catLabels: Record<string, string> = {
    '全部': t('faq_cat_all'),
    '流程': t('faq_cat_process'),
    '医疗': t('faq_cat_medical'),
    '法律': t('faq_cat_legal'),
    '费用': t('faq_cat_cost'),
  };

  const fetchFaqs = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('faqs')
        .select('*')
        .eq('status', 'published')
        .order('sort_order')
        .order('created_at');
      if (err) throw err;
      setFaqs(data ?? []);
    } catch (e) {
      console.error('Failed to fetch FAQs:', e);
      setError(t('faq_load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const filtered = activeCat === '全部' ? faqs : faqs.filter((f) => f.category === activeCat);

  const getQuestion = (f: FAQItem) => f[`question_${lang}`] || f.question_zh;
  const getAnswer = (f: FAQItem) => f[`answer_${lang}`] || f.answer_zh;

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('faq_label')}</div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a5c] mb-3">{t('faq_title')}</h2>
          <p className="text-gray-500 text-sm">{t('faq_subtitle')}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCat(cat); setOpenIndex(null); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCat === cat
                  ? 'bg-[#1a3a5c] text-white'
                  : 'bg-[#f8f5f0] text-gray-500 hover:bg-[#f0e8d8]'
              }`}
            >
              {catLabels[cat] || cat}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block w-6 h-6 border-2 border-[#c9a96e]/30 border-t-[#c9a96e] rounded-full animate-spin mb-3"></div>
            <div className="text-gray-400 text-sm">{t('loading')}</div>
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <i className="ri-error-warning-line text-3xl text-[#c9a96e]/50 mb-3 block"></i>
            <p className="text-gray-500 text-sm mb-4">{error}</p>
            <button
              onClick={fetchFaqs}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1a3a5c] text-white text-sm font-medium hover:bg-[#1a3a5c]/90 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              {t('retry')}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">{t('faq_no_data')}</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((faq, i) => (
              <div
                key={faq.id}
                className={`rounded-xl border transition-all ${
                  openIndex === i ? 'border-[#c9a96e]/40 bg-[#fdf9f3]' : 'border-gray-100 bg-[#f8f5f0]'
                }`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[#c9a96e] text-xs bg-[#c9a96e]/10 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">{faq.category}</span>
                    <span className="font-semibold text-[#1a3a5c] text-sm"><T text={getQuestion(faq)} /></span>
                  </div>
                  <i className={`ri-${openIndex === i ? 'subtract' : 'add'}-line text-[#c9a96e] shrink-0 ml-3 text-lg`}></i>
                </button>
                {openIndex === i && (
                  <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed border-t border-[#c9a96e]/10 pt-3">
                    <T text={getAnswer(faq)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}